import { formatDateOnly, parseIsoDate } from "@/utils/DateFormatter";
import CountryPrefixes from "@/utils/CountryPrefixes";
import type { DocumentAddressDto } from "@/services/UserIdentitiesService";
import type { DocumentProcessingResult } from "@/screen/Identities/Interfaces/IdentityFormInterfaces";
import type { CreateLegalRepresentativeRequest } from "@/services/CompanyOnboardingService";

// Modelo del alta de representante. Solo pide los datos mínimos comprobados en QA para darle de alta
// en Omnia y NextPortfolio (2026-10-29): Pibisi exige fecha de nacimiento y nacionalidad, NextPortfolio
// exige dirección postal (calle, localidad y país) y FATCA. Email y teléfono no los exige el alta, pero
// los necesitará el representante para firmar con OTP. El representante no lleva KYC.
// Las mismas reglas las aplica el backend (LegalRepresentativeRules en IroniaApi).

export type RepresentativeIdType = "DNI" | "NIE";

export interface RepresentativeFormData
{
    // Datos del DNI
    firstName: string;
    lastName: string;
    secondLastName: string;
    idType: RepresentativeIdType;
    idDocument: string;
    birthday: string;
    idExpiration: string;
    /** DNI de validez "PERMANENTE" (mayores de 70 años): no lleva fecha de caducidad. */
    hasPermanentIdExpiration: boolean;
    // Datos adicionales
    nationalCountry: string;
    email: string;
    countryPrefix: string;
    phone: string;
    addressCountry: string;
    zipCode: string;
    city: string;
    state: string;
    roadType: string;
    streetName: string;
    streetNumber: string;
    streetFloor: string;
    streetDoor: string;
    streetOther: string;
    fatcaStatus: string;
}

export type RepresentativeTextField = Exclude<keyof RepresentativeFormData, "hasPermanentIdExpiration" | "idType">;

export interface CatalogOption
{
    value: string;
    text: string;
}

export const FatcaNotSubjectValue = "0";

// TODO: sustituir por el catálogo de GET userIdentities/catalogs.
// Valores tomados del enum FatcaStatuses de UserIdentity en IroniaApi.
export const FatcaStatusOptions: CatalogOption[] =
[
    { value: FatcaNotSubjectValue, text: "No sujeto a comunicación FATCA" },
    { value: "1", text: "Persona estadounidense específica" },
    { value: "2", text: "Entidad pasiva no IF no US y controlling US specified" },
    { value: "3", text: "Institución financiera con titulares US specified" },
    { value: "4", text: "Institución financiera no participante" },
];

export const SpainCountryCode = "ES";

export function getInitialRepresentativeFormData(): RepresentativeFormData
{
    return {
        firstName: "",
        lastName: "",
        secondLastName: "",
        idType: "DNI",
        idDocument: "",
        birthday: "",
        idExpiration: "",
        hasPermanentIdExpiration: false,
        nationalCountry: SpainCountryCode,
        email: "",
        countryPrefix: "+34",
        phone: "",
        addressCountry: SpainCountryCode,
        zipCode: "",
        city: "",
        state: "",
        roadType: "",
        streetName: "",
        streetNumber: "",
        streetFloor: "",
        streetDoor: "",
        streetOther: "",
        fatcaStatus: "",
    };
}

/** Fecha en el formato de los campos type="date" (yyyy-MM-dd); vacío si no se puede interpretar. */
export function toDateInputValue(value: unknown): string
{
    return formatDateOnly(parseIsoDate(value)) ?? "";
}

/** País ISO alfa-2 (el que usan los selectores) a partir del alfa-3 que devuelve la lectura del DNI ("ESP" → "ES"). */
export function toIso2Country(value: string | null | undefined): string
{
    const code = (value ?? "").trim().toUpperCase();
    if(code.length === 2)
    {
        return code;
    }
    return CountryPrefixes.find((country) => country.iso3 === code)?.iso2 ?? "";
}

/**
 * Domicilio leído del DNI en los campos de la dirección postal. El DNI no trae código postal y la calle viene
 * abreviada en una línea, así que solo se rellena lo que se haya leído y el usuario revisa el resto.
 */
function applyAddressReading(data: RepresentativeFormData, address: DocumentAddressDto | undefined): RepresentativeFormData
{
    if(!address)
    {
        return data;
    }

    const firstLine = (address.rawText ?? "").split(/\r?\n/)[0]?.trim() ?? "";
    const streetName = address.road?.trim() || address.streetAddress?.trim() || firstLine;
    const country = toIso2Country(address.countryRegion);

    return {
        ...data,
        streetName: streetName || data.streetName,
        streetNumber: address.houseNumber?.trim() || data.streetNumber,
        streetOther: [address.level, address.unit].filter((part) => !!part && part.trim() !== "").join(" ").trim() || data.streetOther,
        zipCode: address.postalCode?.trim() || data.zipCode,
        city: address.city?.trim() || data.city,
        state: address.state?.trim() || data.state,
        addressCountry: country || data.addressCountry,
    };
}

/**
 * Vuelca en el formulario lo que se ha podido leer del DNI. Lo que no se lea se queda vacío para que el
 * usuario lo complete a mano.
 */
export function applyIdDocumentReading(data: RepresentativeFormData, reading: DocumentProcessingResult): RepresentativeFormData
{
    return {
        ...applyAddressReading(data, reading.address),
        nationalCountry: toIso2Country(reading.nationality) || data.nationalCountry,
        firstName: reading.firstName?.trim() || data.firstName,
        lastName: reading.lastName?.trim() || data.lastName,
        secondLastName: reading.secondLastName?.trim() || data.secondLastName,
        idType: reading.documentType === "NIE" ? "NIE" : "DNI",
        idDocument: reading.documentNumber?.trim().toUpperCase() || data.idDocument,
        birthday: toDateInputValue(reading.dateOfBirth) || data.birthday,
        idExpiration: reading.hasPermanentIdExpiration ? "" : (toDateInputValue(reading.dateOfExpiration) || data.idExpiration),
        hasPermanentIdExpiration: reading.hasPermanentIdExpiration === true || data.hasPermanentIdExpiration,
    };
}

// Obligatorios: los mínimos del alta más la caducidad del DNI (no se admiten documentos caducados)
// y el contacto para la firma. El segundo apellido y el resto de la dirección son opcionales.
export const RequiredRepresentativeFields: RepresentativeTextField[] =
[
    "firstName", "lastName", "idDocument", "birthday", "idExpiration",
    "nationalCountry", "email", "countryPrefix", "phone",
    "addressCountry", "city", "streetName",
    "fatcaStatus",
];

const EmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** FATCA distinto de "No sujeto" impide el alta, igual que en el alta de interviniente. */
export function isFatcaBlocked(data: RepresentativeFormData): boolean
{
    return data.fatcaStatus !== "" && data.fatcaStatus !== FatcaNotSubjectValue;
}

/** Campos obligatorios sin rellenar (o con formato no válido). */
export function getMissingRepresentativeFields(data: RepresentativeFormData): RepresentativeTextField[]
{
    const missing = RequiredRepresentativeFields
        .filter((field) => !(field === "idExpiration" && data.hasPermanentIdExpiration))
        .filter((field) => data[field].trim() === "");
    if(data.email.trim() !== "" && !EmailPattern.test(data.email.trim()))
    {
        missing.push("email");
    }
    return missing;
}

// Nombre de cada campo obligatorio tal como aparece en el formulario
const RequiredRepresentativeFieldLabels: Partial<Record<RepresentativeTextField, string>> =
{
    firstName: "Nombre",
    lastName: "Primer apellido",
    idDocument: "Nº de documento",
    birthday: "Fecha de nacimiento",
    idExpiration: "Fecha de caducidad",
    nationalCountry: "Nacionalidad",
    email: "Correo electrónico",
    countryPrefix: "Prefijo",
    phone: "Teléfono móvil",
    addressCountry: "País",
    city: "Localidad",
    streetName: "Dirección",
    fatcaStatus: "FATCA",
};

/** Nombres de los campos que faltan (o no son válidos) para poder dar de alta al representante. */
export function getMissingRepresentativeFieldLabels(data: RepresentativeFormData): string[]
{
    return Array.from(new Set(getMissingRepresentativeFields(data).map((field) => RequiredRepresentativeFieldLabels[field] ?? field)));
}

export function isRepresentativeFormComplete(data: RepresentativeFormData): boolean
{
    return !isFatcaBlocked(data) && getMissingRepresentativeFields(data).length === 0;
}

const emptyToNull = (value: string): string | null => (value.trim() === "" ? null : value.trim());

/** Petición del alta en el backend (CreateLegalRepresentativeDto). */
export function buildCreateLegalRepresentativeRequest(data: RepresentativeFormData): CreateLegalRepresentativeRequest
{
    return {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        secondLastName: emptyToNull(data.secondLastName),
        idType: data.idType,
        idDocument: data.idDocument.trim().toUpperCase(),
        birthday: data.birthday,
        idExpiration: data.hasPermanentIdExpiration ? null : emptyToNull(data.idExpiration),
        hasPermanentIdExpiration: data.hasPermanentIdExpiration,
        nationalCountry: data.nationalCountry,
        email: data.email.trim(),
        countryPrefix: data.countryPrefix,
        phone: data.phone.replace(/\s+/g, ""),
        postalAddress:
        {
            roadType: emptyToNull(data.roadType),
            streetName: data.streetName.trim(),
            streetNumber: emptyToNull(data.streetNumber),
            streetFloor: emptyToNull(data.streetFloor),
            streetDoor: emptyToNull(data.streetDoor),
            streetOther: emptyToNull(data.streetOther),
            zipCode: emptyToNull(data.zipCode),
            city: data.city.trim(),
            state: emptyToNull(data.state),
            country: data.addressCountry,
        },
        fatcaStatus: Number(data.fatcaStatus),
    };
}
